import { useCallback, useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { FieldStateKey, NON_ASSIGNABLE_VALUE, PermissionKey } from '@/common/constants';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { BankInfo } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';

const placeholder = 'Select bank account';

const useBankAccountForRefundsFields = (
  banks: BankInfo[],
  formattedData: EmployerDto,
  activateMode?: boolean,
  isLoading?: boolean,
): Field[] => {
  const { viewMode } = useSetupEmployerMode();
  const externalPayrollContributionsAccountMethodPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.EXTERNAL_PAYROLL_CONTRIBUTIONS_ACCOUNT_METHOD,
    FieldStateKey.EXTERNAL_PAYROLL_CONTRIBUTIONS_ACCOUNT_METHOD,
  );

  const options = banks.map((bank) => ({
    key: bank.id,
    value: bank.description,
  }));

  const getDefaultValue = useCallback((value?: string) => {
    const bankId = banks.find((bank) => bank.id === value)?.id;

    if (bankId) return bankId;

    if (formattedData?.normalProperties?.externalAccountEnable
      || value === NON_ASSIGNABLE_VALUE) return NON_ASSIGNABLE_VALUE;
    return '';
  }, [banks, formattedData]);

  const fields = useMemo(() => {
    if (isLoading) return [];

    return [
      {
        name: 'externalPushReturnAccount',
        type: FieldTypes.Dropdown,
        disabled: viewMode,
        validator: activateMode ? VALIDATORS.REQUIRED_STRING : VALIDATORS.STRING,
        placeholder,
        singleMode: true,
        label: "Bank account",
        options,
        defaultValue: getDefaultValue(formattedData?.normalProperties?.externalPushReturnAccount),
        value: getDefaultValue(formattedData?.normalProperties?.externalPushReturnAccount),
        showRequireIcon: true,
        ...externalPayrollContributionsAccountMethodPermProps,
      },
    ] as Field[];
  }, [isLoading, viewMode, activateMode, options, getDefaultValue, formattedData, externalPayrollContributionsAccountMethodPermProps]);

  return fields;
};

export default useBankAccountForRefundsFields;
